import type { NavOrderEntry, NavOrderFile } from '../types';
import {
  buildPageIconCatalog,
  filterPageIconCatalog,
  type PageIconPickerChoice,
} from './pageIconCatalog';
import { escapeHtml } from './escapeHtml';

const MS_PREFIX = 'ms:';
const MAX_ICON_LEN = 48;

export function makeNavOrderFile(
  order: Record<string, NavOrderEntry[]>,
  icons?: Record<string, string>
): NavOrderFile {
  const file: NavOrderFile = { version: 2, order };
  if (icons && Object.keys(icons).length > 0) file.icons = icons;
  return file;
}

export type ParsedPageIcon =
  | { kind: 'emoji'; value: string }
  | { kind: 'material'; name: string };

export function parsePageIcon(raw: string): ParsedPageIcon {
  const t = raw.trim();
  if (t.startsWith(MS_PREFIX)) {
    const name = t.slice(MS_PREFIX.length).trim();
    if (/^[a-z0-9_]+$/i.test(name)) return { kind: 'material', name };
  }
  return { kind: 'emoji', value: t || '📄' };
}

export function formatMaterialIcon(name: string): string {
  return `${MS_PREFIX}${name}`;
}

/** HTML for sidebar / page header (trusted: emoji or our material span). */
export function renderPageIconHtml(raw: string, className = 'page-icon'): string {
  const parsed = parsePageIcon(raw);
  if (parsed.kind === 'material') {
    ensureMaterialFont();
    return `<span class="material-symbols-outlined ${className}" aria-hidden="true">${escapeHtml(parsed.name)}</span>`;
  }
  return `<span class="${className} page-icon--emoji" aria-hidden="true">${escapeHtml(parsed.value)}</span>`;
}

/** Heuristic icon when no custom icon is stored. */
export function defaultPageIcon(path: string): string {
  if (path.includes('vineyard')) return '🍇';
  if (path.includes('winemaking')) return '🍷';
  if (path.includes('garden')) return '🥬';
  if (path.includes('notes')) return '📋';
  if (path.includes('home')) return '🔧';
  if (path.includes('trees')) return formatMaterialIcon('forest');
  if (path.includes('grocery')) return '🛒';
  if (path.includes('observations')) return '🔭';
  if (path.includes('recipes')) return formatMaterialIcon('restaurant');
  if (path.includes('welding') || /\/projects\.md$/i.test(path)) return formatMaterialIcon('handyman');
  return '📄';
}

export function pageIcon(path: string, navOrder: NavOrderFile | null): string {
  const custom = navOrder?.icons?.[path];
  if (custom) return custom;
  return defaultPageIcon(path);
}

export function sanitizeNavIcons(raw: unknown): Record<string, string> | undefined {
  if (!raw || typeof raw !== 'object') return undefined;
  const out: Record<string, string> = {};
  for (const [k, v] of Object.entries(raw as Record<string, unknown>)) {
    if (typeof k !== 'string' || typeof v !== 'string' || !v.trim()) continue;
    const t = v.trim().slice(0, MAX_ICON_LEN);
    if (t.startsWith(MS_PREFIX)) {
      const name = t.slice(MS_PREFIX.length);
      if (/^[a-z0-9_]+$/i.test(name)) out[k] = `${MS_PREFIX}${name}`;
    } else {
      out[k] = t;
    }
  }
  return Object.keys(out).length > 0 ? out : undefined;
}

export function patchNavOrderIcons(
  icons: Record<string, string> | undefined,
  oldPath: string,
  newPath: string
): Record<string, string> | undefined {
  if (!icons || !(oldPath in icons)) return icons;
  const next = { ...icons };
  next[newPath] = next[oldPath]!;
  delete next[oldPath];
  return next;
}

export function patchNavOrderIconsForPrefix(
  icons: Record<string, string> | undefined,
  oldPrefix: string,
  newPrefix: string
): Record<string, string> | undefined {
  if (!icons) return icons;
  const next: Record<string, string> = {};
  const map = (p: string) =>
    p === oldPrefix || p.startsWith(`${oldPrefix}/`) ? newPrefix + p.slice(oldPrefix.length) : p;
  for (const [k, v] of Object.entries(icons)) {
    next[map(k)] = v;
  }
  return Object.keys(next).length > 0 ? next : undefined;
}

export function removeNavOrderIcon(
  icons: Record<string, string> | undefined,
  path: string
): Record<string, string> | undefined {
  if (!icons || !(path in icons)) return icons;
  const next = { ...icons };
  delete next[path];
  return Object.keys(next).length > 0 ? next : undefined;
}

let pickerEl: HTMLElement | null = null;

function ensureMaterialFont(): void {
  if (document.querySelector('link[data-page-icon-font]')) return;
  const link = document.createElement('link');
  link.rel = 'stylesheet';
  link.href =
    'https://fonts.googleapis.com/css2?family=Material+Symbols+Outlined:opsz,wght,FILL,GRAD@24,400,0,0&display=swap';
  link.setAttribute('data-page-icon-font', '1');
  document.head.appendChild(link);
}

function ensurePickerModal(): HTMLElement {
  if (pickerEl) return pickerEl;
  ensureMaterialFont();

  const modal = document.createElement('div');
  modal.id = 'page-icon-picker';
  modal.className = 'page-icon-picker hidden';
  modal.setAttribute('role', 'presentation');
  modal.innerHTML = `
    <div class="page-icon-picker-backdrop" aria-hidden="true"></div>
    <div class="page-icon-picker-card" role="dialog" aria-modal="true" aria-labelledby="page-icon-picker-title">
      <h2 id="page-icon-picker-title" class="page-icon-picker-title">Page icon</h2>
      <label class="page-icon-picker-search-wrap">
        <span class="page-icon-picker-search-label">Search</span>
        <input type="search" class="page-icon-picker-search" placeholder="Search emoji and icons…" autocomplete="off" spellcheck="false" />
      </label>
      <p class="page-icon-picker-count" aria-live="polite"></p>
      <div class="page-icon-picker-scroll">
        <ul class="page-icon-picker-grid" role="listbox" aria-label="Icons"></ul>
        <p class="page-icon-picker-empty hidden">No icons match your search.</p>
      </div>
      <div class="page-icon-picker-actions">
        <button type="button" class="btn-ghost-dark page-icon-picker-auto">↩ Auto (default)</button>
        <button type="button" class="btn-ghost-dark page-icon-picker-cancel">Cancel</button>
      </div>
    </div>
  `;
  document.body.appendChild(modal);
  pickerEl = modal;
  return modal;
}

function renderPickerGrid(
  ul: HTMLElement,
  choices: PageIconPickerChoice[],
  selectedValue: string,
  onPick: (value: string) => void
): void {
  ul.innerHTML = '';
  const frag = document.createDocumentFragment();
  for (const choice of choices) {
    const li = document.createElement('li');
    li.setAttribute('role', 'option');
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'page-icon-picker-cell';
    btn.title = choice.displayLabel;
    if (choice.value === selectedValue) btn.classList.add('selected');
    btn.innerHTML = `${renderPageIconHtml(choice.value, 'page-icon-picker-glyph')}<span class="page-icon-picker-cell-label">${escapeHtml(choice.displayLabel)}</span>`;
    btn.addEventListener('click', () => onPick(choice.value));
    li.appendChild(btn);
    frag.appendChild(li);
  }
  ul.appendChild(frag);
}

export async function openPageIconPicker(currentIcon: string): Promise<string | null> {
  ensureMaterialFont();
  const modal = ensurePickerModal();
  const catalog = buildPageIconCatalog();
  const selectedValue = currentIcon.trim();

  return new Promise(resolve => {
    let resolved = false;
    const done = (value: string | null) => {
      if (resolved) return;
      resolved = true;
      modal.classList.add('hidden');
      document.body.style.overflow = '';
      resolve(value);
    };

    const searchInput = modal.querySelector<HTMLInputElement>('.page-icon-picker-search')!;
    const countEl = modal.querySelector<HTMLElement>('.page-icon-picker-count')!;
    const scrollEl = modal.querySelector<HTMLElement>('.page-icon-picker-scroll')!;
    const ul = modal.querySelector<HTMLElement>('.page-icon-picker-grid')!;
    const emptyEl = modal.querySelector<HTMLElement>('.page-icon-picker-empty')!;

    const extra: PageIconPickerChoice[] = [];
    if (selectedValue && !catalog.some(c => c.value === selectedValue)) {
      const parsed = parsePageIcon(selectedValue);
      extra.push({
        value: selectedValue,
        searchText: parsed.kind === 'material' ? parsed.name.replace(/_/g, ' ') : selectedValue,
        displayLabel: parsed.kind === 'material' ? parsed.name.replace(/_/g, ' ') : 'Current',
      });
    }

    const applyFilter = () => {
      const q = searchInput.value;
      let filtered = filterPageIconCatalog(catalog, q);
      if (!q.trim() && extra.length > 0) {
        filtered = [...extra, ...filtered];
      } else if (extra.length > 0) {
        const extraFiltered = filterPageIconCatalog(extra, q);
        if (extraFiltered.length > 0) filtered = [...extraFiltered, ...filtered];
      }

      const total = catalog.length + extra.length;
      countEl.textContent =
        q.trim() === ''
          ? `${total} emoji & icons`
          : `${filtered.length} of ${total} match`;

      if (filtered.length === 0) {
        ul.innerHTML = '';
        emptyEl.classList.remove('hidden');
      } else {
        emptyEl.classList.add('hidden');
        renderPickerGrid(ul, filtered, selectedValue, v => done(v));
      }
    };

    searchInput.value = '';
    applyFilter();
    searchInput.oninput = () => applyFilter();

    modal.querySelector('.page-icon-picker-auto')!.addEventListener('click', () => done(''), { once: true });
    modal.querySelector('.page-icon-picker-cancel')!.addEventListener('click', () => done(null), { once: true });
    modal.querySelector('.page-icon-picker-backdrop')!.addEventListener('click', () => done(null), { once: true });

    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        done(null);
        document.removeEventListener('keydown', onKey);
      }
    };
    document.addEventListener('keydown', onKey);

    modal.classList.remove('hidden');
    document.body.style.overflow = 'hidden';
    requestAnimationFrame(() => {
      searchInput.focus();
      scrollEl.scrollTop = 0;
    });
  });
}
